/* eslint-disable @typescript-eslint/no-unused-vars */
import { Add, AttachFile, Delete, DoneRounded, DownloadRounded, Visibility } from "@mui/icons-material";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import useFetch from "src/Components/Common/useFetch";
import { UseCaseType } from "src/entity/recon-entity/ReconInterfaces";
import {
  BasicFile,
  FileType,
  UploadFileDialog,
  UploadFileType,
} from "../../../BalanceConfirmationPortal/BalanceConfirmationPortalCommon";
import { StateDispatch } from "../../../CommonLegacy/CommonComponents";
import { PartnerCommsContext } from "../../../Context/PartnerCommunicationContext";
import { getShortName } from "../../../LedgerRequestPortal/CommonComponents";
import { BackdropLoader } from "../../../MsmePartnerPortal/PartnerPortal.common";
import $ from "../../../MsmePartnerPortal/PartnerPortal.module.scss";
import BcNewContext from "../BcNewContext";

type UploadAndViewFileSectionProps = {
  fileType: UploadFileType;
  mailAttachmentFiles: { name: string; link: string }[];
  initialFiles: BasicFile[];
  setCompleted?: StateDispatch<{ [k in UploadFileType]: boolean }>;
  updateDocumentCount?: StateDispatch<number>;
};

export const UploadAndViewFileSection = ({
  fileType,
  mailAttachmentFiles,
  initialFiles,
  updateDocumentCount,
}: UploadAndViewFileSectionProps) => {
  const fileUploader = useRef<HTMLInputElement>(null);
  const currentFiles = useRef<File[]>([]);
  const [files, setFiles] = useState<BasicFile[]>(initialFiles || []);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openBackDrop, setOpenBackDrop] = useState<boolean>(false);

  const { currentRow, companyId, branchCode } = useContext(BcNewContext);
  const { communicationTypeId } = useContext(PartnerCommsContext);

  useEffect(() => {
    if (updateDocumentCount) updateDocumentCount(files.length);
  }, [files]);

  const titleBody: { [k in UploadFileType]: { title: string; body: string } } = {
    signedPdf: {
      title: `Signed and Stamped Balance Confirmation Document`,
      body: "Upload the scanned copy of the signed and sealed Balance Confirmation Document",
    },
    ledgerFile: {
      title: `Full Ledger Statement in your ERP/Accounting Software Format`,
      body: "Upload the Full Ledger in excel(.xlsx) format",
    },
    openItem: {
      title: `Open Items in your ERP/Accounting Software Format`,
      body: "Upload the Open Item in excel(.xlsx) format",
    },
  };

  const UploadFiles = (_file?: LinkFile) => {
    const bodyFormData = new FormData();

    bodyFormData.append("type", FileType[fileType]);
    bodyFormData.append("mappingId", currentRow?.mappingId?.toString());
    bodyFormData.append("businessPartnerId", currentRow?.businessPartnerId?.toString());
    bodyFormData.append("useCaseType", UseCaseType.balanceConfirmationBeta);
    bodyFormData.append("communicationTypeId", currentRow?.communicationTypeId?.toString());
    bodyFormData.append("companyId", companyId?.toString());
    bodyFormData.append("branchCode", branchCode?.toString());
    bodyFormData.append("s3Files", JSON.stringify(_file || { name: "", link: "" }));

    for (const item of currentFiles.current) {
      bodyFormData.append("file", item);
    }

    setOpenBackDrop(true);

    return useFetch<{ updatedFilesList: string[] }>("/api/PartnerCommunicationPortal/UploadFilesFromTool", "POST", {
      failureMessage: "Error in uploading file",
      showSuccessToast: true,
      data: bodyFormData,
      thenCallBack: (_res) => {
        setFiles(
          _res.data?.updatedFilesList?.length > -1
            ? _res.data.updatedFilesList?.map((name) => ({ name }))
            : currentFiles.current
        );
        // clear issue in ledger, if re-uploaded ledger

        setOpenDialog(false);
        setOpenBackDrop(false);
      },
      catchCallBack: () => setOpenBackDrop(false),
      errorCallback: () => setOpenBackDrop(false),
    });
  };

  const ViewUploadedFiles = (fileName: string) => {
    const foundFile = files.find((f) => f.name === fileName);

    return useFetch<{ signedUrl?: string }>("/api/PartnerCommunicationPortal/ViewUploadedFilesForTool", "GET", {
      failureMessage: "Error in viewing file",
      showSuccessToast: true,
      config: {
        params: {
          fileName: fileName,
          contentType: foundFile?.type,
          type: FileType[fileType],
          mappingId: currentRow.mappingId,
          businessPartnerId: currentRow.businessPartnerId,
          useCaseType: UseCaseType.balanceConfirmationBeta,
          communicationTypeId: communicationTypeId,
          companyId: companyId,
          branchCode: branchCode,
          isToBeDownloaded: getShortName(fileName)[1].toLowerCase() !== "pdf",
        },
      },
      thenCallBack: (_res) => {
        const link = document.createElement("a");
        link.href = _res.data.signedUrl;
        link.download = fileName;
        link.target = "_blank";
        link.referrerPolicy = "no-referrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
    });
  };

  const DeleteUploadedFiles = (fileName: string) => {
    return useFetch("/api/PartnerCommunicationPortal/DeleteUploadedFilesFromTool", "POST", {
      failureMessage: "Error in deleting file",
      showSuccessToast: true,
      data: {
        fileName: fileName,
        type: FileType[fileType],
        mappingId: currentRow.mappingId,
        businessPartnerId: currentRow.businessPartnerId,
        useCaseType: UseCaseType.balanceConfirmationBeta,
        communicationTypeId: communicationTypeId,
        companyId: companyId,
        branchCode: branchCode,
      },
      thenCallBack: (_res) => {
        setFiles(files.filter((f) => f.name !== fileName));
      },
    });
  };

  return (
    <>
      {/* <Suspense fallback={<LoadingPage noHeader={true} />}> */}
      {/* Uploader Box */}
      <Box className={$.uploader_box + " "}>
        <Box className="vertical_center_align" p={2} gap={1}>
          <Box className="left" mr={"auto"}>
            <Typography variant="subtitle1" fontWeight={700} color={"#000"}>
              {titleBody[fileType].title}
            </Typography>
            <Typography variant="body2">{titleBody[fileType].body}</Typography>
          </Box>

          <Button
            startIcon={<i className="fa-solid fa-arrow-up-from-bracket"></i>}
            onClick={() => {
              fileUploader.current && fileUploader.current.click();
            }}
            sx={{ minWidth: 95 }}
          >
            Upload
          </Button>
        </Box>
        {mailAttachmentFiles?.length > 0 && (
          <MailAttachmentsSection
            mailAttachmentFiles={mailAttachmentFiles}
            uploadFile={async (_file) => {
              await UploadFiles(_file);
            }}
            files={files}
          />
        )}
        {files?.length > 0 &&
          files?.map((file, index) => (
            <CommonFileViewDeleteBox
              key={file.name + index}
              fileName={file.name}
              fileTypePdf={fileType}
              viewFn={ViewUploadedFiles}
              deleteFn={DeleteUploadedFiles}
            />
          ))}
      </Box>
      <input
        ref={fileUploader}
        name="ConfirmDocUploader"
        type="file"
        multiple={true}
        onClick={(_e) => ((_e.target as HTMLInputElement).value = null)}
        onChange={(_e) => {
          const _files = Array.from(_e.target.files);
          currentFiles.current = _files;
          setOpenDialog(true);
        }}
        hidden
        style={{ visibility: "hidden" }}
        accept={
          fileType === "ledgerFile" || fileType === "openItem"
            ? ".xlsx,.xls,.pdf,.xlsm,.xlsb," + (fileType === "openItem" ? ".jpeg,.jpg,.png" : "")
            : ".pdf,.jpeg,.jpg,.png"
        }
      />
      {/* </Suspense> */}
      <UploadFileDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        modalType={fileType}
        uploadFn={UploadFiles}
        consumerComponent={"BC"}
      />
      <BackdropLoader openBackDrop={openBackDrop} loadingText={"Uploading File..."} />
    </>
  );
};

type LinkFile = { name: string; link: string };

const MailAttachmentsSection = ({
  mailAttachmentFiles = [],
  uploadFile,
  files,
}: {
  mailAttachmentFiles: LinkFile[];
  uploadFile: (file: LinkFile) => Promise<void>;
  files: BasicFile[];
}) => (
  <Box sx={{ width: "100%", pb: 2, px: 2 }}>
    <Stack direction="column" spacing={2}>
      <Box sx={{ border: "1px solid #d7d7d7", borderRadius: 1, overflow: "hidden" }}>
        <Stack direction="row" spacing={1.5} sx={{ p: 2, bgcolor: "#e1f5fe" }} alignItems="center">
          <AttachFile sx={{ color: "#014361" }} />
          <Typography variant="subtitle2" sx={{ color: "#014361" }}>
            Attachments detected from Email thread
          </Typography>
          <Typography variant="body2" sx={{ color: "#014361", ml: "auto" }}>
            Click filename to upload or upload manually
          </Typography>
        </Stack>
        <Box sx={{ p: 1.5 }}>
          {mailAttachmentFiles.map((file, index) => (
            <Chip
              key={index}
              label={file.name}
              icon={files.some((currentFile) => currentFile.name === file.name) ? <DoneRounded /> : <Add />}
              onClick={() => {
                if (files.some((currentFile) => currentFile.name === file.name)) {
                  return;
                }
                uploadFile(file);
              }}
              color={files.some((currentFile) => currentFile.name === file.name) ? "primary" : "default"}
              variant="filled"
              size="small"
              sx={{ m: 0.5, "& *": { fontFamily: "inherit" } }}
            />
          ))}
        </Box>
      </Box>
    </Stack>
  </Box>
);

export const CommonFileViewDeleteBox = ({ fileName, fileTypePdf, viewFn, deleteFn }) => {
  return (
    <>
      <Box
        className="d_flex"
        sx={{ borderTop: "1px solid #D7D7D7", background: "#F3F3F3", ":last-child": { borderRadius: "0 0 4px 4px" } }}
        p={2}
        gap={1}
      >
        <Chip
          sx={{ mr: "auto", borderRadius: "8px", maxWidth: "70%" }}
          title={fileName}
          variant="outlined"
          label={
            <div className="vertical_center_align textOverflow_hidden">
              <AttachFile fontSize="small" />
              <Typography className="textOverflow_hidden">{fileName}</Typography>
            </div>
          }
        />
        <Button
          className={$.BR_fix}
          variant="outlined"
          startIcon={fileTypePdf ? <Visibility /> : <DownloadRounded />}
          onClick={() => {
            viewFn(fileName);
          }}
        >
          {fileTypePdf ? `View` : `Download`}
        </Button>
        {
          <Button
            className={$.BR_fix}
            variant="outlined"
            startIcon={<Delete />}
            color="error"
            onClick={() => {
              deleteFn(fileName);
            }}
          >
            Delete
          </Button>
        }
      </Box>
    </>
  );
};
